import React, { useEffect, useState } from 'react';
import { DateRangePicker } from 'react-date-range';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { enUS } from 'date-fns/locale';
import { Popper } from '@mui/material';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Images, SaveParams } from 'utils';
import { ErrMessage } from '../messages';
import { TypableDateInput } from './constants';

export const DateTypeSelector = ({
    outLabel,
    handleGetDates,
    filters,
    buttonStyles,
    startName,
    endName,
    type,
    error,
    reset,
    storageSave,
    disabled,
                                     noCLear,
    backDropIndex,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const info = filters ? filters : location?.state;
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;
    const [state, setState] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        },
    ]);

    const customLocale = {
        ...enUS,
        options: {
            ...enUS.options,
            weekStartsOn: 0,
        },
    };

    // Parse a YYYY-MM-DD string (or moment/Date) into a local-midnight Date object.
    // This prevents the DateRangePicker from shifting dates by one day in
    // western-hemisphere timezones (new Date('YYYY-MM-DD') parses as UTC midnight
    // which becomes the previous day in US timezones).
    const toLocalDate = (dateStr) => {
        const m = moment(dateStr);
        return new Date(m.year(), m.month(), m.date());
    };

    useEffect(() => {
        if (info?.[startName] || info?.[endName]) {
            const params = {
                startDate: info[startName] ? toLocalDate(info[startName]) : toLocalDate(moment()),
                endDate: info[endName] ? toLocalDate(info[endName]) : toLocalDate(moment()),
                key: 'selection',
            };

            if (startName !== 'startDate' && startName !== 'endDate') {
                info[startName] ? (params[startName] = moment.utc(info[startName]).format('YYYY-MM-DD')) : delete params[startName];
            }
            if (endName !== 'startDate' && endName !== 'endDate') {
                info[endName] ? (params[endName] = moment.utc(info[endName]).format('YYYY-MM-DD')) : delete params[endName];
            }
            setState([params]);
        } else {
            const params = {
                startDate: toLocalDate(moment()),
                endDate: toLocalDate(moment()),
                key: 'selection',
            };
            delete params[startName];
            delete params[endName];
            setState([params]);
        }
    }, [info]);

    const handleDateChange = (item) => {
        const startDate = moment(item?.selection?.startDate);
        const endDate = moment(item?.selection?.endDate);

        const stateParams = {
            startDate: toLocalDate(startDate),
            endDate: toLocalDate(endDate),
            key: 'selection',
        };
        if (startName !== 'startDate' && startName !== 'endDate') {
            stateParams[startName] = startDate.format('YYYY-MM-DD');
        }
        if (endName !== 'startDate' && endName !== 'endDate') {
            stateParams[endName] = endDate.format('YYYY-MM-DD');
        }
        setState([stateParams]);

        const params = {
            ...info,
        };
        params[startName] = startDate.format('YYYY-MM-DD');
        params[endName] = endDate.format('YYYY-MM-DD');

        if (reset) {
            params.page = 1;
            params.limit = 50;
            params.skip = 0;
        }

        if (handleGetDates) {
            handleGetDates(params);
        } else {
            if (storageSave) {
                storageSave(params);
            }
            SaveParams(navigate, params);
        }
    };

    const handleClear = () => {
        setState([
            {
                startDate: toLocalDate(moment()),
                endDate: toLocalDate(moment()),
                key: 'selection',
            },
        ]);

        const params = {
            ...info,
        };

        delete params[startName];
        delete params[endName];

        if (reset) {
            params.page = 1;
            params.limit = 50;
            params.skip = 0;
        }

        if (handleGetDates) {
            handleGetDates(params);
        } else {
            if (storageSave) {
                storageSave(params);
            }
            SaveParams(navigate, params);
        }
    };

    const handleClick = (event) => {
        if (disabled) return;
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleTypeDate = (value, name, originalName) => {
        const date = value?.toString();

        if (isValidDate(date)) {
            const typedValue = moment.utc(value, 'YYYY-MM-DD');

            const stateParams = {
                ...state[0],
            };
            stateParams[name] = typedValue.format('YYYY-MM-DD');
            stateParams[originalName] = toLocalDate(typedValue);
            setState([stateParams]);
        }
    };

    const handleBlur = (name) => {
        const value = state[0][name];
        const checkIsNotSame =
            value && info?.[name] ? moment.utc(value).format('YYYY-MM-DD') !== moment.utc(info?.[name]).format('YYYY-MM-DD') : true;

        if (value && checkIsNotSame) {
            if (handleGetDates) {
                const params = {
                    ...info,
                };
                params[name] = moment.utc(value).format('YYYY-MM-DD');
                handleGetDates(params);
            } else {
                const params = {
                    ...info,
                };
                params[name] = moment.utc(value).format('YYYY-MM-DD');
                if (storageSave) {
                    storageSave(params);
                }
                SaveParams(navigate, params);
            }
        }
    };

    const isValidDate = (dateString) => {
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    };

    return (
        <div className="date-selector-box">
            {outLabel && (
                <p
                    id='labelTitle'
                    style={
                        type === 'modalInput' ? { color: error ? '#FE7070' : info[startName] || info[endName] ? '#347AF0' : '#51566D' } : {}
                    }
                    className={type === 'modalInput' ? 'modal-date-selector-label' : 'date-selector-label'}
                >
                    {outLabel}
                </p>
            )}
            <button
                style={
                    error ? { borderColor: '#FE7070', color: '#FE7070', ...buttonStyles } : type === 'modalInput' ? {} : { ...buttonStyles }
                }
                className={type === 'modalInput' ? 'modal-date-range-selector' : 'date-range-selector'}
                aria-describedby={id}
                type="button"
                onClick={handleClick}
            >
                <div
                    className="date-range-inputs"
                    style={{ gap: type === 'modalInput' ? 16 : 4 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                >
                    <TypableDateInput
                        disabled={disabled}
                        onBlur={() => handleBlur(startName)}
                        placeholder={'mm/dd/yyyy'}
                        value={state[0]?.[startName] ? moment.utc(state[0][startName]).toDate() : null}
                        onChange={(date) => {
                            if (date && new Date(date) > new Date(2045, 11, 31)) return;
                            handleTypeDate(date, startName, 'startDate');
                        }}
                    />
                    <img
                        style={{
                            filter: 'brightness(0) saturate(100%) invert(18%) sepia(4%) saturate(5385%) hue-rotate(179deg) brightness(96%) contrast(78%)',
                            width: 16,
                        }}
                        src={Images.arrowRight}
                        alt="icon"
                    />
                    <TypableDateInput
                        disabled={disabled}
                        onBlur={() => handleBlur(endName)}
                        placeholder={'mm/dd/yyyy'}
                        value={state[0]?.[endName] ? moment.utc(state[0][endName]).toDate() : null}
                        onChange={(date) => {
                            if (date && new Date(date) > new Date(2045, 11, 31)) return;
                            handleTypeDate(date, endName, 'endDate');
                        }}
                    />
                </div>
                <div className="flex-align-center">
                    <div style={{ width: '21px' }}>
                        {(info?.[startName] || info?.[endName]) && !noCLear && (
                            <button
                                className="remove-calendar-day"
                                type={'button'}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleClear();
                                }}
                            >
                                <img src={Images.removeBlack} alt="remove" />
                            </button>
                        )}
                    </div>
                    <img src={Images.calendarToday} alt="icon" />
                </div>
            </button>

            {error && <ErrMessage text={error} />}

            <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                className={'custom-date-selector-box'}
                placement="bottom-start"
                style={{ position: 'absolute', zIndex: 1400 }}
            >
                <DateRangePicker showDateDisplay={false} ranges={state} onChange={handleDateChange} locale={customLocale} />
                {!noCLear &&
                <button className="clear-bnt" onClick={handleClear}>
                    Clear
                </button>
                }
            </Popper>
            {open && <div className="beck-drop" onClick={() => setAnchorEl(null)} />}
        </div>
    );
};
